// Mutation hooks for Admin > Users — the write-side complement to the read
// hooks in hooks.ts. Everything goes through `api` (the CSRF header is added
// automatically for non-GET), and every success prefix-invalidates the users
// list, the open user detail, and the audit feed so all three refetch together.

import { useQueryClient } from '@tanstack/react-query';
import { useRegisteredMutation } from './mutation';
import type { QueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Role, UserStatus } from './types.gen';
import type { UserActionResponse } from './types';
import { adminAudit, adminAuditRecent, adminUserById, adminUsers } from './resources';

// Read off the resources rather than spelled out. These were three literal
// tuples that had to be kept in step with `queries.ts` by hand — rename a key
// there and every one of them becomes a silent no-op: no type error, no failing
// test, the directory just stops refreshing after an invite or a role change.
const ADMIN_PREFIXES = [adminUsers.all, adminUserById.all, adminAudit.all, adminAuditRecent.all] as const;

function invalidateAdmin(qc: QueryClient) {
  for (const queryKey of ADMIN_PREFIXES) {
    void qc.invalidateQueries({ queryKey });
  }
}

export interface InviteUserBody {
  email: string;
  name: string;
  role: Role;
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: (body: InviteUserBody) =>
      api.post<UserActionResponse>('/admin/users/invite', { body }),
    onSuccess: () => invalidateAdmin(qc),
  });
}

export interface UserPatch {
  role?: Role;
  status?: UserStatus;
}

export function usePatchUser() {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: ({ id, ...patch }: { id: string } & UserPatch) =>
      api.patch<UserActionResponse>(`/admin/users/${encodeURIComponent(id)}`, { body: patch }),
    onSuccess: () => invalidateAdmin(qc),
  });
}

function useUserAction(action: string) {
  const qc = useQueryClient();
  return useRegisteredMutation({
    domains: ['identity_rbac'],
    mutationFn: (id: string) =>
      api.post<UserActionResponse>(`/admin/users/${encodeURIComponent(id)}/${action}`),
    onSuccess: () => invalidateAdmin(qc),
  });
}

export function useDisableUser() {
  return useUserAction('disable');
}

export function useEnableUser() {
  return useUserAction('enable');
}

/** Re-mints the invite token (invalidating the old link) and re-sends the email. */
export function useResendInvite() {
  return useUserAction('resend-invite');
}

/** Admin-issued reset link (1-hour expiry) for an active user. */
export function useIssuePasswordReset() {
  return useUserAction('reset-password');
}

export function useDeleteUser() {
  return useUserAction('delete');
}
